import { useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { serverEncryption } from "@/shared/utils/serverEncryption";

interface SavePassportNumberParams {
  passportNumber: string;
  estateId: number;
}

interface UseSavePassportNumberResult {
  savePassportNumber: (params: SavePassportNumberParams) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function useSavePassportNumber(): UseSavePassportNumberResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ passportNumber, estateId }: SavePassportNumberParams) => {
      if (!estateId) {
        throw new Error("Person ID is required");
      }

      const encrypted = await serverEncryption.encrypt(
        passportNumber,
        "person",
        estateId,
        "passport_number",
      );

      const { error } = await supabase
        .from("estates")
        .update({ passport_number_encrypted: encrypted })
        .eq("estate_id", estateId);

      if (error) {
        throw new Error(`Failed to save encrypted passport number: ${error.message}`);
      }
    },
    onSuccess: (_result, { estateId }) => {
      queryClient.invalidateQueries({ queryKey: ["personData", estateId] });
      queryClient.invalidateQueries({ queryKey: ["estates"] });
      queryClient.invalidateQueries({ queryKey: ["deceasedPassportData", estateId] });
    },
  });

  return {
    savePassportNumber: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error?.message || null,
  };
}
